import { useState } from 'react';

import { Button, Input } from '@/components/ui';
import type {
  Attempt,
  AttemptConclusion,
  AttemptState,
  AttemptTransition,
} from '@/lib/admin/client';

type AttemptTransitionTarget = 'submitted' | `completed:${AttemptConclusion}`;

const ACTIVE_ATTEMPT_STATES: AttemptState[] = [
  'leased',
  'in_progress',
  'submitted',
];
const TRANSITION_OPTIONS: Array<{
  label: string;
  value: AttemptTransitionTarget;
}> = [
  { label: 'Submitted', value: 'submitted' },
  { label: 'Completed · accepted', value: 'completed:accepted' },
  {
    label: 'Completed · retry required',
    value: 'completed:retry_required',
  },
  {
    label: 'Completed · lease reclaimed',
    value: 'completed:lease_reclaimed',
  },
];

function isAttemptTransitionTarget(
  value: string
): value is AttemptTransitionTarget {
  return TRANSITION_OPTIONS.some((option) => option.value === value);
}

export type AttemptTransitionControlsProps = {
  attempt: Attempt;
  isPending: boolean;
  onTransition: (attemptId: string, transition: AttemptTransition) => void;
};

export function AttemptTransitionControls({
  attempt,
  isPending,
  onTransition,
}: AttemptTransitionControlsProps) {
  const [target, setTarget] = useState<AttemptTransitionTarget | ''>('');
  const [reason, setReason] = useState('');
  const [headBranch, setHeadBranch] = useState('');
  const [commitSha, setCommitSha] = useState('');

  if (!ACTIVE_ATTEMPT_STATES.includes(attempt.state)) return null;

  const options = TRANSITION_OPTIONS.filter(
    (option) => option.value !== attempt.state
  );
  const selectedTarget = options.some((option) => option.value === target)
    ? target
    : '';
  const conclusionReasonRequired =
    selectedTarget === 'completed:retry_required' ||
    selectedTarget === 'completed:lease_reclaimed';
  const conclusionReasonMissing =
    conclusionReasonRequired && reason.trim() === '';
  const publicationIdentityMissing =
    selectedTarget === 'submitted' &&
    (headBranch.trim() === '' || commitSha.trim() === '');
  const disabled =
    selectedTarget === '' ||
    conclusionReasonMissing ||
    publicationIdentityMissing ||
    isPending;

  const submit = () => {
    if (selectedTarget.startsWith('completed:')) {
      const conclusion = selectedTarget.slice(
        'completed:'.length
      ) as AttemptConclusion;
      onTransition(attempt.attemptId, {
        state: 'completed',
        conclusion,
        ...(reason.trim() ? { conclusionReason: reason.trim() } : {}),
      });
      return;
    }
    if (selectedTarget === 'submitted') {
      onTransition(attempt.attemptId, {
        state: 'submitted',
        headBranch: headBranch.trim(),
        commitSha: commitSha.trim(),
      });
    }
  };

  return (
    <div className="bg-muted/30 basis-full space-y-2 rounded-md p-2">
      <div className="flex flex-wrap items-end gap-2">
        <label className="min-w-48 flex-1 space-y-1 text-sm">
          <span className="text-muted-foreground text-xs font-medium uppercase">
            Transition
          </span>
          <select
            aria-label={`Transition attempt ${attempt.attemptId}`}
            value={selectedTarget}
            onChange={(event) => {
              const nextTarget = event.target.value;
              setTarget(
                isAttemptTransitionTarget(nextTarget) ? nextTarget : ''
              );
            }}
            disabled={isPending}
            className="border-input bg-background h-9 w-full rounded-md border px-3 text-sm"
          >
            <option value="">Select transition</option>
            {options.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </label>
        {conclusionReasonRequired && (
          <label className="min-w-64 flex-[2] space-y-1 text-sm">
            <span className="text-muted-foreground text-xs font-medium uppercase">
              Conclusion reason
            </span>
            <Input
              aria-label={`Conclusion reason for attempt ${attempt.attemptId}`}
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              disabled={isPending}
            />
          </label>
        )}
        {selectedTarget === 'submitted' && (
          <>
            <label className="min-w-64 flex-[2] space-y-1 text-sm">
              <span className="text-muted-foreground text-xs font-medium uppercase">
                Head branch
              </span>
              <Input
                aria-label={`Head branch for attempt ${attempt.attemptId}`}
                value={headBranch}
                onChange={(event) => setHeadBranch(event.target.value)}
                disabled={isPending}
              />
            </label>
            <label className="min-w-64 flex-[2] space-y-1 text-sm">
              <span className="text-muted-foreground text-xs font-medium uppercase">
                Commit SHA
              </span>
              <Input
                aria-label={`Commit SHA for attempt ${attempt.attemptId}`}
                value={commitSha}
                onChange={(event) => setCommitSha(event.target.value)}
                disabled={isPending}
              />
            </label>
          </>
        )}
        <Button
          type="button"
          size="sm"
          variant="outline"
          aria-label={`Apply transition to attempt ${attempt.attemptId}`}
          onClick={submit}
          disabled={disabled}
        >
          Apply transition
        </Button>
      </div>
    </div>
  );
}
